import React, { useState, useEffect } from 'react';

import { useNavigate, Link } from 'react-router-dom';
import db from "./Firebase";
import { CSVLink } from "react-csv";
import Moment from 'moment';
import { extendMoment } from 'moment-range';

function UsersLastCheckin() {
    const navigate = useNavigate();
    const moment = extendMoment(Moment);

    const [mergedData, setMergedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [lastVisible, setLastVisible] = useState(null);
    const [loading, setLoading] = useState(false);
    const [allDataFetched, setAllDataFetched] = useState(false);
    const [loadLimit, setLoadLimit] = useState(20); // Default 20 records

    const headers = [
        { label: "Name", key: "full_name" },
        { label: "Phone No", key: "phone_no" },
        { label: "Username", key: "username" },
        { label: "Last Checkin", key: "last_checkin", dataFormat: "MM/DD/YY, h:mm a" },
    ];

    useEffect(() => {
        const storedUsername = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");

        if (storedRemember === "false" || !storedUsername || !storedPassword) {
            navigate('/signin');
        }
    }, [navigate]);

    const fetchPersonalDetails = async (userIds) => {
        const personalDetailsMap = {};

        // Firestore allows max 10 elements in "in" query, so we batch requests
        const chunkSize = 10;
        for (let i = 0; i < userIds.length; i += chunkSize) {
            const chunk = userIds.slice(i, i + chunkSize);
            const snapshot = await db.collection("personal_details").where('__name__', 'in', chunk).get();

            snapshot.docs.forEach(doc => {
                personalDetailsMap[doc.id] = doc.data();
            });
        }

        return personalDetailsMap;
    };

    const fetchData = async (limit) => {
        if (allDataFetched) return;
        setLoading(true);

        let query = db.collection("user_checkin").orderBy("last_checkin", "desc").limit(limit);
        if (lastVisible) query = query.startAfter(lastVisible);

        const userCheckinSnapshot = await query.get();
        if (userCheckinSnapshot.empty) {
            setAllDataFetched(true);
            setLoading(false);
            return;
        }

        const userCheckinData = userCheckinSnapshot.docs.map(doc => ({
            uid: doc.id,
            last_checkin: doc.data().last_checkin,
        }));

        setLastVisible(userCheckinSnapshot.docs[userCheckinSnapshot.docs.length - 1]);

        // Fetch personal details in batches of 10
        const userIds = userCheckinData.map(user => user.uid);
        const personalDetailsMap = await fetchPersonalDetails(userIds);

        const merged = userCheckinData.map(user => ({
            full_name: personalDetailsMap[user.uid]?.full_name || 'N/A',
            phone_no: personalDetailsMap[user.uid]?.phone_no || 'N/A',
            username: personalDetailsMap[user.uid]?.username || 'N/A',
            last_checkin: user.last_checkin,
        }));

        setMergedData(prevData => [...prevData, ...merged]);
        setLoading(false);
    };


    // Initial Data Load (20 Records)
    useEffect(() => {
        fetchData(20);
    }, []);

    // Load More Data
    const handleLoadMore = (limit) => {
        setLoadLimit(limit);
        fetchData(limit);
    };

    // Search Function
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredData = mergedData.filter(({ full_name, phone_no }) => {
        const query = searchQuery.toLowerCase();
        return (
            (full_name && full_name.toLowerCase().includes(query)) ||
            (phone_no && phone_no.includes(searchQuery))
        );
    });

    // CSV Data
    const csvData = filteredData.map(({ full_name, phone_no, username, last_checkin }) => ({
        full_name,
        phone_no,
        username,
        last_checkin: moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a"),
    }));

    return (
        <>
            <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg ">
                <div className="p-2 d-flex justify-content-between">
                    <Link to="/">
                        <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
                    </Link>
                    <CSVLink data={csvData} headers={headers} filename="users_last_checkin.csv">
                        <span className="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span>
                    </CSVLink>
                </div>

                <div className="p-2">
                    <div className="card">
                        <div className="card-header pb-0 px-3">
                            <h6 className="mb-2">Users Last Checkin</h6>
                        </div>

                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search by name / phone_no"
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ maxWidth: "300px" }}
                        />

                        <div className="table-responsive fix-width scroll-inner">
                            <table className="table table-bordered table-hover">
                                <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Full Name</th>
                                        <th>Phone</th>
                                        <th>Username</th>
                                        <th>Last Checkin</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map(({ full_name, phone_no, username, last_checkin }, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{full_name}</td>
                                            <td>{phone_no}</td>
                                            <td>{username}</td>
                                            <td>{moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button className="btn btn-primary me-2" onClick={() => handleLoadMore(100)}>Load 100</button>
                            <button className="btn btn-primary me-2" onClick={() => handleLoadMore(200)}>Load 200</button>
                            <button className="btn btn-secondary" onClick={() => handleLoadMore(loadLimit)} disabled={allDataFetched}>
                                {allDataFetched ? "All Data Loaded" : `Load More (${loadLimit})`}
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default UsersLastCheckin;


// import React, { useState, useEffect } from 'react';

// import { useNavigate, Link } from 'react-router-dom';
// import db from "./Firebase";
// import { CSVLink } from "react-csv";
// import Moment from 'moment';
// import { extendMoment } from 'moment-range';

// function UsersLastCheckin() {
//     const navigate = useNavigate();
//     const moment = extendMoment(Moment);
//     const [mergedData, setMergedData] = useState([]);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [lastVisible, setLastVisible] = useState(null);
//     const [hasMore, setHasMore] = useState(true);

//     const headers = [
//         { label: "Name", key: "full_name" },
//         { label: "Phone No", key: "phone_no" },
//         { label: "Username", key: "username" },
//         { label: "Last Checkin", key: "last_checkin", dataFormat: "MM/DD/YY, h:mm a" },
//     ];

//     useEffect(() => {
//         const storedUsername = localStorage.getItem("email");
//         const storedPassword = localStorage.getItem("password");
//         const storedRemember = localStorage.getItem("remember");

//         if (storedRemember === "false" || !storedUsername || !storedPassword) {
//             navigate('/signin');
//         }
//     }, []);

//     const fetchData = async () => {
//         const userCheckinSnapshot = await db.collection("user_checkin")
//             .orderBy("last_checkin", "desc")
//             .limit(20)
//             .get();

//         const lastVisibleDoc = userCheckinSnapshot.docs[userCheckinSnapshot.docs.length - 1];
//         setLastVisible(lastVisibleDoc);

//         const userCheckinData = userCheckinSnapshot.docs.map((doc) => ({
//             uid: doc.id,
//             last_checkin: doc.data().last_checkin,
//         }));

//         const personalDetailsSnapshot = await db.collection("personal_details").get();
//         const personalDetailsData = personalDetailsSnapshot.docs.map((doc) => ({
//             uid: doc.id,
//             full_name: doc.data().full_name,
//             phone_no: doc.data().phone_no,
//             username: doc.data().username,
//         }));

//         const merged = userCheckinData.map((userCheckin) => {
//             const personalDetails = personalDetailsData.find((personal) => personal.uid === userCheckin.uid);
//             return {
//                 ...personalDetails,
//                 last_checkin: userCheckin.last_checkin,
//             };
//         });

//         setMergedData(merged);
//     };

//     useEffect(() => {
//         fetchData();
//     }, []);

//     const loadMoreData = async () => {
//         const userCheckinSnapshot = await db.collection("user_checkin")
//             .orderBy("last_checkin", "desc")
//             .startAfter(lastVisible)
//             .limit(20)
//             .get();

//         const lastVisibleDoc = userCheckinSnapshot.docs[userCheckinSnapshot.docs.length - 1];
//         setLastVisible(lastVisibleDoc);

//         if (userCheckinSnapshot.docs.length < 20) {
//             setHasMore(false);
//         }

//         const userCheckinData = userCheckinSnapshot.docs.map((doc) => ({
//             uid: doc.id,
//             last_checkin: doc.data().last_checkin,
//         }));

//         const personalDetailsSnapshot = await db.collection("personal_details").get();
//         const personalDetailsData = personalDetailsSnapshot.docs.map((doc) => ({
//             uid: doc.id,
//             full_name: doc.data().full_name,
//             phone_no: doc.data().phone_no,
//             username: doc.data().username,
//         }));

//         const merged = userCheckinData.map((userCheckin) => {
//             const personalDetails = personalDetailsData.find((personal) => personal.uid === userCheckin.uid);
//             return {
//                 ...personalDetails,
//                 last_checkin: userCheckin.last_checkin,
//             };
//         });

//         setMergedData(prevData => [...prevData, ...merged]);
//     };

//     const handleSearch = (event) => {
//         setSearchQuery(event.target.value);
//     };

//     const filteredData = mergedData.filter(({ full_name, phone_no }) => {
//         const query = searchQuery.toLowerCase();
//         return (
//             (full_name && full_name.toLowerCase().includes(query)) ||
//             (phone_no && phone_no.includes(searchQuery))
//         );
//     });

//     const csvData = filteredData.map(({ full_name, phone_no, username, last_checkin }) => ({
//         full_name,
//         phone_no,
//         username,
//         last_checkin: moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a"),
//     }));

//     return (
//         <>
//             <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg ">
//                 <div className="p-2" style={{ paddingBottom: "5px", display: "flex", justifyContent: "space-between" }}>
//                     <Link to="/"><img src="/images/menubutton.png" className="menu-toggle" alt="Responsive image" style={{ height: "2rem" }} /></Link>
//                     <CSVLink data={csvData} headers={headers} filename={"users_last_checkin.csv"}>
//                         <span className="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span>
//                     </CSVLink>
//                 </div>
//                 <div className="">
//                     <div className="p-2">
//                         <div className="card">
//                             <div className="card-header pb-0 px-3">
//                                 <h6 className="mb-2">Users Last Checkin</h6>
//                             </div>
//                             <input
//                                 type="text"
//                                 className="form-control form-control-sm"
//                                 placeholder="Search by name / phone_no"
//                                 value={searchQuery}
//                                 onChange={handleSearch}
//                             />
//                             <div className="">
//                                 <div className="card">
//                                     <div className="table-responsive fix-width scroll-inner">
//                                         <table className="table table-bordered table-hover">
//                                             <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
//                                                 <tr style={{ textAlign: "left" }}>
//                                                     <th className=" ps-2">Full Name</th>
//                                                     <th className="">Phone</th>
//                                                     <th className=" ps-2">Username</th>
//                                                     <th className="">Last Checkin</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody style={{ textAlign: "left" }}>
//                                                 {filteredData.map(({ full_name, phone_no, username, last_checkin }) => (
//                                                     <tr key={full_name}>
//                                                         <td>
//                                                             <p className="text-xs font-weight-bold mb-0">{full_name}</p>
//                                                         </td>
//                                                         <td>
//                                                             <div className="d-flex px-2 py-1">
//                                                                 <div className="d-flex flex-column justify-content-center">
//                                                                     <h6 className="mb-0 text-xs">{phone_no}</h6>
//                                                                 </div>
//                                                             </div>
//                                                         </td>
//                                                         <td>
//                                                             <div className="d-flex px-2 py-1">
//                                                                 <div className="d-flex flex-column justify-content-center">
//                                                                     <h6 className="mb-0 text-xs">{username}</h6>
//                                                                 </div>
//                                                             </div>
//                                                         </td>
//                                                         <td>
//                                                             <div className="d-flex px-2 py-1">
//                                                                 <div className="d-flex flex-column justify-content-center">
//                                                                     <h6 className="mb-0 text-xs">{moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a")}</h6>
//                                                                 </div>
//                                                             </div>
//                                                         </td>
//                                                     </tr>
//                                                 ))}
//                                             </tbody>
//                                         </table>
//                                         {hasMore && (
//                                             <div className="" style={{ paddingTop: "2rem" }}>
//                                                 <button onClick={loadMoreData} className="btn btn-dark">Load More</button>
//                                             </div>
//                                         )}
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </main>
//         </>
//     )
// }

// export default UsersLastCheckin;




// // import React, { useState, useEffect } from 'react';

// // import { useNavigate, Link } from 'react-router-dom';
// // import db from "./Firebase";
// // import { CSVLink } from "react-csv";
// // import Moment from 'moment';
// // import { extendMoment } from 'moment-range';

// // function UsersLastCheckin() {
// //     const navigate = useNavigate();
// //     const moment = extendMoment(Moment);
// //     const [mergedData, setMergedData] = useState([]);
// //     const [searchQuery, setSearchQuery] = useState('');

// //     const headers = [
// //         { label: "Name", key: "full_name" },
// //         { label: "Phone No", key: "phone_no" },
// //         { label: "Username", key: "username" },
// //         { label: "Last Checkin", key: "last_checkin", dataFormat: "MM/DD/YY, h:mm a" },
// //     ];

// //     useEffect(() => {
// //         const unsubscribeUserCheckin = db.collection("user_checkin").onSnapshot((userCheckinSnapshot) => {
// //             const userCheckinData = userCheckinSnapshot.docs.map((doc) => ({
// //                 uid: doc.id,
// //                 last_checkin: doc.data().last_checkin,
// //             }));

// //             const unsubscribePersonalDetails = db.collection("personal_details").onSnapshot((personalDetailsSnapshot) => {
// //                 const personalDetailsData = personalDetailsSnapshot.docs.map((doc) => ({
// //                     uid: doc.id,
// //                     full_name: doc.data().full_name,
// //                     phone_no: doc.data().phone_no,
// //                     username: doc.data().username,
// //                 }));

// //                 const merged = userCheckinData.map((userCheckin) => {
// //                     const personalDetails = personalDetailsData.find((personal) => personal.uid === userCheckin.uid);
// //                     return {
// //                         ...personalDetails,
// //                         last_checkin: userCheckin.last_checkin,
// //                     };
// //                 });

// //                 setMergedData(merged);
// //             });

// //             return () => {
// //                 unsubscribeUserCheckin();
// //                 unsubscribePersonalDetails();
// //             };
// //         });
// //     }, []);

// //     const handleSearch = (event) => {
// //         setSearchQuery(event.target.value);
// //     };


// //     const filteredData = mergedData.filter(({ full_name, phone_no }) => {
// //         const query = searchQuery.toLowerCase();
// //         // Check if full_name and phone_no are defined before calling toLowerCase()
// //         return (
// //             (full_name && full_name.toLowerCase().includes(query)) ||
// //             (phone_no && phone_no.includes(searchQuery))
// //         );
// //     });

// //     const csvData = filteredData.map(({ full_name, phone_no, username, last_checkin }) => ({
// //         full_name,
// //         phone_no,
// //         username,
// //         last_checkin: moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a"),
// //     }));

// //     return (
// //         <>
// //             <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg ">
// //                 <div className="p-2" style={{ paddingBottom: "5px", display: "flex", justifyContent: "space-between" }}>
// //                     <Link to="/"><img src="/images/menubutton.png" className="menu-toggle" alt="Responsive image" style={{ height: "2rem" }} /></Link>
// //                     <CSVLink data={csvData} headers={headers} filename={"users_last_checkin.csv"}>
// //                         <span className="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span>
// //                     </CSVLink>
// //                 </div>
// //                 <div className="">
// //                     <div className="p-2">
// //                         <div className="card">
// //                             <div className="card-header pb-0 px-3">
// //                                 <h6 className="mb-2">Users Last Checkin</h6>
// //                             </div>
// //                             <input
// //                                 type="text"
// //                                 className="form-control form-control-sm"
// //                                 placeholder="Search by name / phone_no"
// //                                 value={searchQuery}
// //                                 onChange={handleSearch}
// //                             />
// //                             <div className="">
// //                                 <div className="card">
// //                                     <div className="table-responsive fix-width scroll-inner">
// //                                         <table className="table table-bordered table-hover">
// //                                             <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
// //                                                 <tr style={{ textAlign: "left" }}>
// //                                                     <th className=" ps-2">Full Name</th>
// //                                                     <th className="">Phone</th>
// //                                                     <th className=" ps-2">Username</th>
// //                                                     <th className="">Last Checkin</th>
// //                                                 </tr>
// //                                             </thead>
// //                                             <tbody style={{ textAlign: "left" }}>
// //                                                 {filteredData.map(({ full_name, phone_no, username, last_checkin }) => (
// //                                                     <tr key={full_name}>
// //                                                         <td>
// //                                                             <p className="text-xs font-weight-bold mb-0">{full_name}</p>
// //                                                         </td>
// //                                                         <td>
// //                                                             <div className="d-flex px-2 py-1">
// //                                                                 <div className="d-flex flex-column justify-content-center">
// //                                                                     <h6 className="mb-0 text-xs">{phone_no}</h6>
// //                                                                 </div>
// //                                                             </div>
// //                                                         </td>
// //                                                         <td>
// //                                                             <div className="d-flex px-2 py-1">
// //                                                                 <div className="d-flex flex-column justify-content-center">
// //                                                                     <h6 className="mb-0 text-xs">{username}</h6>
// //                                                                 </div>
// //                                                             </div>
// //                                                         </td>
// //                                                         <td>
// //                                                             <div className="d-flex px-2 py-1">
// //                                                                 <div className="d-flex flex-column justify-content-center">
// //                                                                     <h6 className="mb-0 text-xs">{moment(last_checkin.toDate()).format("MM/DD/YY, h:mm a")}</h6>
// //                                                                 </div>
// //                                                             </div>
// //                                                         </td>
// //                                                     </tr>
// //                                                 ))}
// //                                             </tbody>
// //                                         </table>
// //                                     </div>
// //                                 </div>
// //                             </div>
// //                         </div>
// //                     </div>
// //                 </div>
// //             </main>
// //         </>
// //     )
// // }

// // export default UsersLastCheckin;
